import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';

function mediaSrc(msg) {
  return resolveMemberMediaUrl(msg?.mediaUrl || msg?.objectKey || '');
}

export function TextMessage({ message }) {
  const text = message?.text || '';
  return <p className="chat-msg__text">{renderPlainText(text)}</p>;
}

/** Preserve newlines; never inject HTML. */
export function renderPlainText(text) {
  const value = String(text ?? '');
  const parts = value.split('\n');
  return parts.map((line, i) => (
    <span key={i}>
      {line}
      {i < parts.length - 1 ? <br /> : null}
    </span>
  ));
}

export function ImageMessage({ message }) {
  const src = mediaSrc(message);
  if (!src) return <p className="chat-msg__fallback">[圖片]</p>;
  return (
    <a className="chat-msg__media-link" href={src} target="_blank" rel="noreferrer">
      <img className="chat-msg__image" src={src} alt={message.fileName || 'image'} loading="lazy" />
    </a>
  );
}

export function StickerMessage({ message }) {
  const src = mediaSrc(message);
  return (
    <div className="chat-msg__sticker">
      {src ? <img src={src} alt={message.emoteCode || 'sticker'} /> : <span>{message.emoteCode || '[貼圖]'}</span>}
      {message.publisherDisplayName ? (
        <small className="chat-msg__meta">{message.publisherDisplayName}</small>
      ) : null}
      {message.accessType ? <small className="chat-msg__meta">{message.accessType}</small> : null}
    </div>
  );
}

export function GifMessage({ message }) {
  const src = mediaSrc(message);
  return src ? (
    <img className="chat-msg__gif" src={src} alt={message.emoteCode || 'gif'} loading="lazy" />
  ) : (
    <p className="chat-msg__fallback">{message.emoteCode || '[GIF]'}</p>
  );
}

export function AudioMessage({ message }) {
  const src = mediaSrc(message);
  return src ? (
    <audio className="chat-msg__audio" controls preload="metadata" src={src}>
      您的瀏覽器不支援音訊
    </audio>
  ) : (
    <p className="chat-msg__fallback">[語音]</p>
  );
}

export function VideoMessage({ message }) {
  const src = mediaSrc(message);
  const cover = resolveMemberMediaUrl(message.coverMediaUrl || message.coverObjectKey || '');
  return src ? (
    <video className="chat-msg__video" controls preload="metadata" poster={cover || undefined} src={src}>
      您的瀏覽器不支援影片
    </video>
  ) : (
    <p className="chat-msg__fallback">[影片]</p>
  );
}

export function FileMessage({ message }) {
  const src = mediaSrc(message);
  const name = message.fileName || '檔案';
  return (
    <a className="chat-msg__file" href={src || '#'} target="_blank" rel="noreferrer" download={name}>
      <span className="chat-msg__file-name" title={name}>
        {name}
      </span>
      {message.fileSize != null ? (
        <small>{formatSize(message.fileSize)}</small>
      ) : null}
    </a>
  );
}

export function LocationMessage({ message }) {
  const { latitude, longitude, locationName, locationAddress } = message || {};
  const maps =
    latitude != null && longitude != null
      ? `https://maps.google.com/?q=${latitude},${longitude}`
      : null;
  return (
    <div className="chat-msg__location">
      <strong>{locationName || '[位置]'}</strong>
      {locationAddress ? <p>{locationAddress}</p> : null}
      {maps ? (
        <a href={maps} target="_blank" rel="noreferrer">
          開啟地圖 ({latitude}, {longitude})
        </a>
      ) : null}
    </div>
  );
}

export function MapPointCard({ message }) {
  const card = message?.mapPoint || {};
  const cover = resolveMemberMediaUrl(card.coverMediaUrl || card.coverObjectKey || '');
  return (
    <div className="chat-msg__card">
      {cover ? <img src={cover} alt="" /> : null}
      <div>
        <strong>{card.title || '[地圖點]'}</strong>
        {card.address ? <p>{card.address}</p> : null}
      </div>
    </div>
  );
}

export function ItineraryCard({ message }) {
  const card = message?.itinerary || {};
  const cover = resolveMemberMediaUrl(card.coverMediaUrl || card.coverObjectKey || '');
  return (
    <div className="chat-msg__card">
      {cover ? <img src={cover} alt="" /> : null}
      <div>
        <strong>{card.title || '[行程]'}</strong>
        {card.summary ? <p>{card.summary}</p> : null}
      </div>
    </div>
  );
}

export function GroupTripCard({ message }) {
  const card = message?.groupTrip || {};
  const cover = resolveMemberMediaUrl(card.coverMediaUrl || card.coverObjectKey || '');
  return (
    <div className="chat-msg__card">
      {cover ? <img src={cover} alt="" /> : null}
      <div>
        <strong>{card.title || '[揪團]'}</strong>
        {card.description || card.summary ? <p>{card.description || card.summary}</p> : null}
      </div>
    </div>
  );
}

export function SocialPostCard({ message }) {
  const card = message?.socialPost || {};
  const cover = resolveMemberMediaUrl(card.coverMediaUrl || card.coverObjectKey || '');
  const meta = [card.authorDisplayName, card.mapPointTitle, card.city].filter(Boolean).join(' · ');
  return (
    <div className="chat-msg__card">
      {cover ? <img src={cover} alt="" /> : null}
      <div>
        <strong>{card.title || '[貼文]'}</strong>
        {card.description ? <p>{card.description}</p> : null}
        {meta ? <p className="chat-msg__card-meta">{meta}</p> : null}
      </div>
    </div>
  );
}

export function SystemMessage({ message }) {
  return <p className="chat-msg__system">{message?.text || '[系統訊息]'}</p>;
}

export function UnsupportedMessage({ message }) {
  return (
    <p className="chat-msg__unsupported">
      不支援的訊息類型：{message?.messageType || 'UNKNOWN'}
    </p>
  );
}

export function RevokedMessage() {
  return <p className="chat-msg__revoked">訊息已撤回</p>;
}

export function TextWithSegments({ message }) {
  const segments = message?.segments;
  if (!Array.isArray(segments) || !segments.length) {
    return <TextMessage message={message} />;
  }
  return (
    <p className="chat-msg__text chat-msg__segments">
      {segments.map((seg, i) => {
        if (seg.type === 'STICKER' && seg.mediaUrl) {
          return (
            <img
              key={i}
              className="chat-msg__inline-emote"
              src={resolveMemberMediaUrl(seg.mediaUrl || seg.objectKey)}
              alt={seg.emoteCode || ''}
              title={seg.emoteCode}
            />
          );
        }
        if (seg.type === 'MENTION') {
          const label = seg.text || `@${seg.mentionUsername || seg.mentionDisplayName || ''}`;
          return (
            <span
              key={i}
              className="chat-msg__mention"
              title={seg.mentionDisplayName || seg.mentionUsername || ''}
              data-member-id={seg.mentionedMemberId}
            >
              {label}
            </span>
          );
        }
        // No permission / unknown → show raw code as text (server decision)
        return <span key={i}>{renderPlainText(seg.text || seg.emoteCode || '')}</span>;
      })}
    </p>
  );
}

const REGISTRY = {
  TEXT: TextWithSegments,
  IMAGE: ImageMessage,
  STICKER: StickerMessage,
  GIF: GifMessage,
  AUDIO: AudioMessage,
  VIDEO: VideoMessage,
  FILE: FileMessage,
  LOCATION: LocationMessage,
  MAP_POINT: MapPointCard,
  ITINERARY: ItineraryCard,
  GROUP_TRIP: GroupTripCard,
  SOCIAL_POST: SocialPostCard,
  SYSTEM: SystemMessage,
};

export function MessageBody({ message }) {
  if (!message) return null;
  if (message.status === 'REVOKED') {
    return <RevokedMessage />;
  }
  const Comp = REGISTRY[message.messageType] || UnsupportedMessage;
  try {
    return <Comp message={message} />;
  } catch {
    return <UnsupportedMessage message={message} />;
  }
}

function formatSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
